<template>
	<main>
		<div v-if="masterListData && masterListData.master_list_onsub.length > 0 && masterDetailData" class="container">
			<MasterList
				v-if="!isMasterListLoading"
				:master-list="masterListData ?? []"
				:all="true"
				@update:master-pk="onChangeMasterPk"
			/>

			<section class="section_characterInfo">
				<div class="characterInfo_btnContainer">
					<button @click="getHTSExcel">
						HTS 다운로드<img src="@/assets/img/common/download.svg" alt="HTS 다운로드 아이콘" loading="lazy" />
					</button>
					<button @click="getSimpleInfoExcel">
						간편정보 다운로드<img
							src="@/assets/img/common/download.svg"
							alt="간편정보 다운로드 아이콘"
							loading="lazy"
						/>
					</button>
				</div>
				<div v-if="masterDetailData.master" class="characterInfo_infoContainer">
					<img :src="masterDetailData.master.file_path" class="infoContainer_img" alt="대가 일러스트" loading="lazy" />
					<div class="infoContainer_content">
						<p class="content_name">{{ masterDetailData?.master.master_name }}</p>
						<p class="content_moto">{{ masterDetailData?.master.master_motto }}</p>
						<p v-if="masterDetailData?.master.master_rebalance_date" class="content_date">
							{{ masterDetailData?.master.master_rebalance_date }}
						</p>
					</div>
				</div>
			</section>
			<section class="section_stock">
				<ToolTipTabBar :tab="STOCK_MENU_TAB_LIST" @update:master-event="onStockTabClick" />
				<div v-if="masterEventData" class="stock_lists" :class="displayStore.isDesktop && 'stock_list_desktop'">
					<PointList
						v-if="masterEventData?.master_investment"
						:data="masterEventData?.master_investment.slice(0, 45) ?? []"
						:master-pk="Number(routePk)"
						:subscribe="1"
						@update:book-mark-refetch="fetchMasterEvent"
					/>
				</div>
			</section>
		</div>
		<!-- <div v-else class="container lock c_inner">
			<div class="lock_img">
				<img src="@/assets/img/common/exclamation.png" alt="lock" loading="lazy" />
			</div>
			<div class="lock_txt">
				<p>구독한 내역이 없습니다.</p>
				<p>
					대가의 선택 구독 시 미국, 일본 등 해외주식에 대한
					<br />
					대가의 포착 종목과 Hot랭킹, 회원 텔레그램 등 다양한 서비스를 확인할 수 있습니다.
				</p>
			</div>
			<button class="lock_btn" @click="router.push('/payment')">대가의선택 구독하기</button>
		</div> -->
	</main>
	<HomePopup :exposure-location="'subscription'" />
</template>

<script setup lang="ts">
// lib
import { computed, onBeforeMount, watch, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { AxiosResponse } from 'axios';

// api
import { api } from '@/api/_http';
import { useGetMasterDetailApi, useGetMasterEventApi, useGetMasterListApi } from '@/api/master';
import { GetHTSPathDataType, useGetHTSPathApi, useGetSimpleInfoExcelApi } from '@/api/subscribe';
// component
import ToolTipTabBar from '@/components/atom/tabBar/ToolTipTabBar.vue';
import HomePopup from '@/components/popup/HomePopup.vue';
import MasterList from '@/components/rankList/MasterList.vue';
import PointList from '@/components/rankList/PointList.vue';
// constant
import { STOCK_MENU_TAB_LIST } from '@/constants/title';
// store
import { useDisplayStore } from '@/stores/display';
import { useTokenStore } from '@/stores/token';
// util
import { openNativeExcelDownload } from '@/utils/native';
import { useGetCheckAuthApi } from '@/api/auth';

// -----variable-----
const route = useRoute();
const router = useRouter();
const tokenStore = useTokenStore();
const displayStore = useDisplayStore();

const currentSubTab = computed(() => {
	return STOCK_MENU_TAB_LIST[route.query.tab ? Number(route.query.tab) : 3].current ?? '';
});

const routePk = computed(() => {
	return route.query.masterPk ?? '';
});
// -----api-----
const { refetch: refetchCheckAuth } = useGetCheckAuthApi(
	computed(() => ({
		num: 'ALL',
		student: false,
		enable: false,
	}))
);
const { data: masterListData, isLoading: isMasterListLoading } = useGetMasterListApi();
const { data: masterDetailData, refetch: refetchMasterDetail } = useGetMasterDetailApi(
	computed(() => {
		return {
			master_pk: Number(routePk.value),
		};
	})
);
const { data: masterEventData, refetch: refetchMasterEvent } = useGetMasterEventApi(
	computed(() => {
		return {
			master_pk: Number(routePk.value),
			sub_yn: 1,
			master_investment_category: currentSubTab.value,
		};
	})
);
const { data: HTSExcelPathData, refetch: refetchHTSExcelPath } = useGetHTSPathApi(
	computed(() => {
		return {
			master_pk: Number(routePk.value),
		};
	})
);
const { data: SimpleInfoExcelData, refetch: refetchSimpleInfoExcel } = useGetSimpleInfoExcelApi(
	computed(() => {
		return {
			master_pk: Number(routePk.value),
		};
	})
);

// -----function-----
// 대가 변경 시 함수
const onChangeMasterPk = async (pk: number) => {
	router.replace({ query: { masterPk: pk, tab: 3 } });
};

// 주식 탭 클릭 시 함수
const onStockTabClick = async (idx: number) => {
	router.replace({ query: { ...route.query, tab: idx } });
};

// 마스터 리스트 패치 함수
const fetchMasterEvent = async () => {
	await refetchMasterEvent();
};

// HTS 엑셀 다운로드 함수
const DownloadHTS = async (param: GetHTSPathDataType) => {
	openNativeExcelDownload(`https:${param.file.file_path}`);

	const response: AxiosResponse<ArrayBuffer> = await api.post(
		`${import.meta.env.VITE_BASE_URL}/v1/front/getHTSExcel`,
		{
			master_pk: Number(routePk.value),
			access_token: tokenStore.getAccessToken,
		},
		{
			responseType: 'arraybuffer',
		}
	);

	const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' });
	const a = document.createElement('a');
	a.href = window.URL.createObjectURL(blob);
	if (param.file.file_original_name) {
		a.download = param.file.file_original_name;
	}

	a.style.display = 'none';
	document.body.appendChild(a);
	a.click();

	window.URL.revokeObjectURL(a.href);
	document.body.removeChild(a);
};

// 간편 정보 엑셀 다운로드 함수
const DownloadSimpleInfo = async (filePath: string) => {
	openNativeExcelDownload(filePath);

	const a = document.createElement('a');
	a.href = filePath;
	a.style.display = 'none';
	document.body.appendChild(a);
	a.click();

	window.URL.revokeObjectURL(a.href);
	document.body.removeChild(a);
};

// HTS 다운로드 버튼 클릭 시
const getHTSExcel = async () => {
	await refetchHTSExcelPath();
	if (HTSExcelPathData.value) DownloadHTS(HTSExcelPathData.value);
};

// 간편정보 다운로드 버튼 클릭 시
const getSimpleInfoExcel = async () => {
	await refetchSimpleInfoExcel();
	if (SimpleInfoExcelData.value?.file_path) DownloadSimpleInfo(SimpleInfoExcelData.value?.file_path);
};
// 마스터 pk 없는 경우 첫번째 마스터 pk로 변경
const handleGetMasterPk = () => {
	if (masterListData.value && masterListData.value?.master_list_onsub.length > 0) {
		router.replace({ query: { masterPk: masterListData.value?.master_list_onsub[0].master_pk, tab: 3 } });
		return;
	}
	if (masterListData.value && masterListData.value?.master_list_nosub.length > 0) {
		router.replace({ query: { masterPk: masterListData.value?.master_list_nosub[0].master_pk, tab: 3 } });
	}
};

watch(
	() => currentSubTab.value,
	async () => {
		await refetchMasterEvent();
	}
);

watchEffect(async () => {
	if (masterListData.value && !routePk.value) {
		handleGetMasterPk();
	}
	if (routePk.value) {
		await refetchMasterDetail();
		await refetchMasterEvent();
	}
});

onBeforeMount(async () => {
	const res = await refetchCheckAuth();
	if (!res.data?.success) {
		router.replace(`/subscribe/kr/not`);
	}
});
</script>

<style lang="scss" scoped>
.container {
	display: flex;
	flex-direction: column;
	gap: 8.8rem;
	max-width: 68rem;
	padding-top: 7.2rem;
	margin: 0 auto;
	margin-top: var(--header-height);
	margin-bottom: 8.8rem;
	@media screen and (max-width: 1280px) {
		gap: 8rem;
		padding-top: 4rem;
		padding-bottom: 4rem;
	}
}
.section_characterInfo {
	@media screen and (max-width: 1280px) {
		margin-left: var(--space-large);
		margin-right: var(--space-large);
	}
	display: flex;
	flex-direction: column;
	gap: 2rem;
	.characterInfo_btnContainer {
		display: flex;
		flex-direction: row;
		justify-content: end;
		gap: 1rem;
		white-space: nowrap;
		@media screen and (max-width: 324px) {
			flex-direction: column;
		}
		button {
			display: flex;
			justify-content: center;
			align-items: center;
			gap: 0.8rem;
			font-size: var(--font-s-mid);
			font-weight: var(--font-w-medium);
			line-height: 1.79rem;
			padding: 1.3rem 1.6rem;
			border: 1px solid var(--color-base-mid);
			border-radius: 0.6rem;
			img {
				width: 1.6rem;
				height: 1.6rem;
				object-fit: none;
			}
		}
	}
	.characterInfo_infoContainer {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 0.8rem;
		background-color: var(--color-bg-mid);
		padding: 2.4rem 2rem;
		border-radius: 0.8rem;
		border: 1px solid var(--color-base-mid);
		.infoContainer_img {
			height: 10rem;
			object-fit: contain;
		}
		.infoContainer_content {
			display: flex;
			flex-direction: column;
			align-items: center;
			text-align: center;
			gap: var(--space-xx-small);
			.content_name {
				font-size: var(--font-s-x-large);
				font-weight: var(--font-w-semi-bold);
				line-height: 2.387rem;
			}
			.content_moto {
				font-size: var(--font-s-regular);
				font-weight: var(--font-w-light);
				line-height: 2.3rem;
				color: var(--color-base-light);
			}
			.content_date {
				font-size: var(--font-s-small);
				font-weight: var(--font-w-light);
				line-height: 1.551rem;
				color: var(--color-base-mid);
			}
		}
	}
}
.section_stock {
	display: flex;
	flex-direction: column;
	gap: 3.2rem;
	.stock_lists {
		@media screen and (max-width: 1280px) {
			margin-left: var(--space-large);
			margin-right: var(--space-large);
		}
		display: grid;
		gap: 10px;
	}
	.stock_list_desktop .list_item:nth-child(1) {
		grid-column: span 2;
	}
	.stock_list_desktop .list_item:nth-child(n + 2) {
		grid-column: span 1;
	}
}
.container.lock {
	justify-content: center;
	align-items: center;
	gap: 0;
	height: calc(100dvh - var(--header-height) - var(--footer-height) - 5px);
	padding-top: 0;
	margin-top: var(--header-height);
	margin-bottom: 0;
	@media screen and (max-width: 1280px) {
		height: calc(100dvh - var(--header-height) - var(--bottomMenu-height) - 5px);
	}
	.lock_img {
		width: 12rem;
		height: 12rem;
		margin-bottom: 2.4rem;
	}
	.lock_txt {
		display: flex;
		flex-direction: column;
		gap: 0.8rem;
		text-align: center;
		margin-bottom: 3.2rem;
		p:nth-child(1) {
			font-size: var(--font-s-x-large);
			font-weight: var(--font-w-bold);
			line-height: 2.387rem;
		}
		p:nth-child(2) {
			font-size: var(--font-s-regular);
			font-weight: var(--font-w-light);
			line-height: 2.3rem;
		}
	}
	.lock_btn {
		width: 24rem;
		padding: 1.35rem 1.6rem;
		font-size: var(--font-s-regular);
		font-weight: var(--font-w-medium);
		background-color: var(--color-primary);
		border-radius: 0.6rem;
	}
}
</style>
